import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_URL } from '@utils/constants';

export const PORTAL_ACCESS_TOKEN_KEY = 'portalAccessToken';
export const PORTAL_REFRESH_TOKEN_KEY = 'portalRefreshToken';

const portalApi: AxiosInstance = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

portalApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem(PORTAL_ACCESS_TOKEN_KEY);
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    console.log(`[portalApi] -> ${(config.method ?? 'GET').toUpperCase()} ${config.url}`);
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

portalApi.interceptors.response.use(
  (response) => {
    console.log(
      `[portalApi] <- ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
    );
    return response;
  },
  async (error: AxiosError) => {
    console.error(
      `[portalApi] <- error ${error.response?.status ?? '(no response)'} ${error.config?.method?.toUpperCase()} ${error.config?.url}: ${error.message}`,
    );
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const requestUrl = originalRequest.url ?? '';
    const isAuthEndpoint =
      requestUrl.includes('/customer-auth/login') ||
      requestUrl.includes('/customer-auth/refresh') ||
      requestUrl.includes('/customer-auth/forgot-password') ||
      requestUrl.includes('/customer-auth/reset-password') ||
      requestUrl.includes('/customer-auth/accept-invite');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;
      try {
        const refreshToken = localStorage.getItem(PORTAL_REFRESH_TOKEN_KEY);
        if (!refreshToken) {
          throw new Error('No portal refresh token');
        }
        const { data } = await axios.post(`${API_URL}/customer-auth/refresh`, { refreshToken });
        localStorage.setItem(PORTAL_ACCESS_TOKEN_KEY, data.accessToken);
        localStorage.setItem(PORTAL_REFRESH_TOKEN_KEY, data.refreshToken);
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
        }
        return portalApi(originalRequest);
      } catch {
        localStorage.removeItem(PORTAL_ACCESS_TOKEN_KEY);
        localStorage.removeItem(PORTAL_REFRESH_TOKEN_KEY);
        if (!window.location.pathname.startsWith('/portal/login')) {
          window.location.href = '/portal/login';
        }
      }
    }
    return Promise.reject(error);
  },
);

export default portalApi;
